<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Announcement Management' : 'Deleted Announcements'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all system announcements' : 'Restore or permanently delete announcements'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/announcements/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Announcement
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Announcements</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Announcements"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                    placeholder="Search by title or content..."
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-toggle-switch"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Audience Type Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.audience_type"
                    :items="audienceTypes"
                    item-text="text"
                    item-value="value"
                    label="Audience Type"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-account-group"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Publication Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.publication_status"
                    :items="publicationStatuses"
                    item-text="text"
                    item-value="value"
                    label="Publication"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-publish"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Show Deleted Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.show_deleted"
                    :items="deletedStatuses"
                    item-text="text"
                    item-value="value"
                    label="View Type"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-eye"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Export Button -->
                <v-col cols="12" md="1" class="d-flex align-center">
                  <v-btn
                    color="success"
                    outlined
                    small
                    @click="generateExcel()"
                    :loading="isExcelloading"
                    :disabled="!announcements.length || isDeletePage()"
                    class="px-2"
                  >
                    <v-icon small>mdi-file-excel</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-text class="px-0 py-0">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-12">
                <health-loader message="Loading announcements..." />
              </div>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="announcements"
                :items-per-page="-1"
                hide-default-footer
                class="elevation-0"
                :class="{ 'empty-table': !announcements.length }"
              >
                <template v-slot:[`item.sr_no`]="{ index }">
                  <span class="text-body-2">{{
                    (page - 1) * rowsPerPage + index + 1
                  }}</span>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        small
                        :color="item.is_active ? 'success' : 'error'"
                        text-color="white"
                        class="font-weight-medium status-chip"
                      >
                        <v-icon left small>
                          {{
                            item.is_active
                              ? "mdi-check-circle"
                              : "mdi-close-circle"
                          }}
                        </v-icon>
                        {{ item.is_active ? "Active" : "Inactive" }}
                      </v-chip>
                    </template>
                    <span>{{
                      item.is_active
                        ? "Announcement is Active"
                        : "Announcement is Inactive"
                    }}</span>
                  </v-tooltip>
                </template>

                <template v-slot:[`item.title`]="{ item }">
                  <div
                    class="text-body-2 font-weight-medium text-truncate"
                    style="max-width: 420px"
                  >
                    {{ item.title }}
                  </div>
                  <div
                    v-if="item.message"
                    class="text-caption grey--text text-truncate"
                    style="max-width: 420px"
                  >
                    {{ item.message }}
                  </div>
                </template>

                <template v-slot:[`item.audience`]="{ item }">
                  <v-chip
                    small
                    color="info"
                    text-color="white"
                    class="font-weight-medium status-chip"
                  >
                    {{ getAudienceTypeName(item.audience_type) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.publication`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        small
                        :color="getPublicationStatusColor(item)"
                        text-color="white"
                        class="font-weight-medium status-chip"
                      >
                        <v-icon left small>
                          {{ getPublicationStatusIcon(item) }}
                        </v-icon>
                        {{ getPublicationStatus(item) }}
                      </v-chip>
                    </template>
                    <span>{{ getPublicationStatusTooltip(item) }}</span>
                  </v-tooltip>
                </template>

                <template v-slot:[`item.created_by`]="{ item }">
                  <span class="text-body-2">
                    {{ getCreatedByName(item.created_by) }}
                  </span>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                  <span class="text-body-2">{{
                    formatDate(item.created_at)
                  }}</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex justify-end align-center">
                    <template v-if="!isDeletePage()">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            :to="`/announcements/${encryptIt(item.id)}`"
                            icon
                            small
                            color="primary"
                            class="mr-1 action-btn"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit announcement</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            @click="openDeleteDialog(item)"
                            icon
                            small
                            color="error"
                            class="action-btn"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete announcement</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            @click="Restore_this(item.id)"
                            icon
                            small
                            color="success"
                            class="action-btn"
                          >
                            <v-icon small>mdi-delete-restore</v-icon>
                          </v-btn>
                        </template>
                        <span>Restore announcement</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <template v-slot:no-data>
                  <empty-state icon="mdi-bullhorn-outline" :title="isDeletePage() ? &quot;No deleted announcements found&quot; : &quot;No announcements available&quot;" :message="isDeletePage() ? &quot;All announcements are currently active&quot; : &quot;Create your first announcement to get started&quot;" />
                </template>
              </v-data-table>

              <!-- Pagination -->
              <div
                v-if="announcements.length && paginationLength > 1"
                class="px-4 py-3"
              >
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex align-center justify-space-between">
                  <div class="text-body-2 grey--text">
                    Showing {{ (page - 1) * rowsPerPage + 1 }} to
                    {{ Math.min(page * rowsPerPage, count) }} of
                    {{ count }} entries
                  </div>
                  <v-pagination
                    v-model="page"
                    :length="paginationLength"
                    :total-visible="7"
                    circle
                    color="primary"
                    @input="getData()"
                  ></v-pagination>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="deleteLoading"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />

      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_announcement" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Announcement restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_announcement = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  name: "AnnouncementListPage",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      announcements: [],
      isLoading: true,
      isExcelloading: false,
      page: 1,
      count: 0,
      rowsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      is_deleted_announcement: false,
      is_restored_announcement: false,
      deleteDialog: false,
      itemToDelete: null,
      deleteLoading: false,
      headers: [
        { text: "Sr No", value: "sr_no", width: "80px", sortable: false },
        { text: "Status", value: "status", width: "100px", sortable: false },
        { text: "Title & Subtitle", value: "title", sortable: false },
        {
          text: "Audience",
          value: "audience",
          width: "120px",
          sortable: false,
        },
        {
          text: "Publication",
          value: "publication",
          width: "130px",
          sortable: false,
        },
        {
          text: "Created By",
          value: "created_by",
          width: "120px",
          sortable: false,
        },
        {
          text: "Created At",
          value: "created_at",
          width: "150px",
          sortable: false,
        },
        {
          text: "Actions",
          value: "actions",
          width: "100px",
          sortable: false,
          align: "end",
        },
      ],
      filter: {
        search_keyword: null,
        audience_type: null,
        publication_status: null,
        is_active: null,
        show_deleted: null,
      },
      audienceTypes: [
        { text: "All Users", value: "all_users" },
        { text: "Doctors", value: "doctors" },
        { text: "Patients", value: "patients" },
      ],
      publicationStatuses: [
        { text: "Published", value: "published" },
        { text: "Draft", value: "draft" },
        { text: "Scheduled", value: "scheduled" },
      ],
      statuses: [
        {
          id: 0,
          text: "Select Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      deletedStatuses: [
        { text: "Active Announcements", value: false },
        { text: "Deleted Announcements", value: true },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.audience_type ||
        this.filter.publication_status ||
        this.filter.show_deleted
      );
    },
  },

  mounted() {
    this.getData();
  },

  methods: {
    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active !== null && this.filter.is_active !== "")
        count++;
      if (this.filter.audience_type) count++;
      if (this.filter.publication_status) count++;
      if (
        this.filter.show_deleted !== null &&
        this.filter.show_deleted !== false
      )
        count++;
      return count;
    },

    clearAllFilters() {
      this.filter.search_keyword = null;
      this.filter.is_active = null;
      this.filter.audience_type = null;
      this.filter.publication_status = null;
      this.filter.show_deleted = null;
      this.page = 1;
      this.getData();
    },

    async getData() {
      this.isLoading = true;

      // Set default values for filters if they are empty
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.audience_type = this.filter.audience_type || "";
      this.filter.publication_status = this.filter.publication_status || "";
      this.filter.show_deleted = this.filter.show_deleted || false;

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&audience_type=" +
        this.filter.audience_type +
        "&publication_status=" +
        this.filter.publication_status +
        "&search_keyword=" +
        this.filter.search_keyword;

      await axios
        .get(`announcements?${query}`)
        .then((response) => {
          this.announcements = response.data.data.data || response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/announcements/delete/${this.encryptIt(this.itemToDelete.id)}`);
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.deleteLoading = false;
    },
    async Delete_this(id) {
      await axios
        .post(`/announcements/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.is_deleted_announcement = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    async Restore_this(id) {
      await axios
        .post(`/announcements/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.is_restored_announcement = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    async generateExcel() {
      this.isExcelloading = true;

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&audience_type=" +
        this.filter.audience_type +
        "&publication_status=" +
        this.filter.publication_status +
        "&search_keyword=" +
        this.filter.search_keyword;

      await axios
        .get(`announcements/excel?${query}`, { responseType: "blob" })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const fileName = `announcements_export_${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isExcelloading = false;
    },

    isDeletePage() {
      return this.filter.show_deleted === true;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    getAudienceTypeName(audienceTypeId) {
      const audienceType = this.audienceTypes.find(
        (type) => type.value === audienceTypeId
      );
      return audienceType ? audienceType.text : "Unknown";
    },

    getCreatedByName(createdBy) {
      if (!createdBy) return "N/A";
      const name = [createdBy.first_name, createdBy.last_name]
        .filter(Boolean)
        .join(" ");
      return name || createdBy.user_name || "N/A";
    },

    getPublicationStatus(announcement) {
      if (!announcement.is_active) return "Inactive";
      if (!announcement.published_at) return "Draft";

      const publishedAt = new Date(announcement.published_at);
      const now = new Date();

      if (publishedAt > now) return "Scheduled";
      return "Published";
    },

    getPublicationStatusColor(announcement) {
      const status = this.getPublicationStatus(announcement);
      switch (status) {
        case "Published":
          return "green";
        case "Draft":
          return "orange";
        case "Scheduled":
          return "blue";
        case "Inactive":
          return "red";
        default:
          return "grey";
      }
    },

    getPublicationStatusIcon(announcement) {
      const status = this.getPublicationStatus(announcement);
      switch (status) {
        case "Published":
          return "mdi-publish";
        case "Draft":
          return "mdi-file-document-outline";
        case "Scheduled":
          return "mdi-clock-outline";
        case "Inactive":
          return "mdi-close-circle";
        default:
          return "mdi-help-circle";
      }
    },

    getPublicationStatusTooltip(announcement) {
      const status = this.getPublicationStatus(announcement);
      switch (status) {
        case "Published":
          return `Published on ${this.formatDate(announcement.published_at)}`;
        case "Draft":
          return "Not yet published";
        case "Scheduled":
          return `Scheduled for ${this.formatDate(announcement.published_at)}`;
        case "Inactive":
          return "Announcement is inactive";
        default:
          return "Unknown status";
      }
    },
  },
};
</script>

<style scoped>
/* Modern table styling following Products page */
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.v-data-table >>> thead th {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6 !important;
  color: #495057 !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  padding: 12px 16px !important;
}

.v-data-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.v-data-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.v-data-table >>> tbody td {
  padding: 12px 16px !important;
  font-size: 0.875rem;
}

/* Empty state styling */
.empty-table {
  min-height: 400px;
}

/* Filter section styling */
.v-card-subtitle {
  border-bottom: 1px solid #f0f0f0;
}

/* Consistent with Products page header */
.text-h6 {
  font-size: 1.1rem !important;
}

.text-caption {
  font-size: 0.75rem !important;
}

/* Action buttons styling */
.v-btn--icon.v-size--x-small {
  width: 28px !important;
  height: 28px !important;
}

.action-btn.v-btn--icon {
  width: 36px !important;
  height: 36px !important;
}

.action-btn.v-btn--icon .v-icon {
  font-size: 20px !important;
}

/* Chip styling consistency */
.v-chip--x-small {
  height: 20px !important;
  font-size: 0.625rem !important;
}

/* Status/Audience/Publication chips on the announcements list */
.status-chip.v-chip {
  height: 28px !important;
  font-size: 0.875rem !important;
  padding: 0 12px !important;
}

.status-chip.v-chip .v-icon {
  font-size: 18px !important;
}

/* Pagination styling */
.v-pagination {
  justify-content: center;
}

.v-pagination >>> .v-pagination__item {
  min-width: 32px !important;
  height: 32px !important;
  font-size: 0.875rem !important;
}
</style>